import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useGetProgramExpenseById } from "../../../../../../../../api/services/client/clientApis";
import {
  ViewExpenseDetails,
  ViewFeeOrExpenseDetails,
} from "../../../../../../../../api/services/program/models";
import {
  ITabs,
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiModal,
  MuiTabPanel,
  MuiTabs,
  MuiTypography,
  MuiViewMorePopOver,
} from "../../../../../../../../components";
import { TaxDetailsFormKeyProps } from "../../../../../../../../components/taxDetails/modelsAndSchemas/Models";
import { TaxDetails } from "../../../../../../../../components/taxDetails/TaxDetails";
import { CURRENCY_FORMAT_MAP } from "../../../../../../../../components/textFieldCurrency/CurrencyUtils";
import {
  EMPTY_VALUE_DASH,
  viewMorePopOverGridSize,
} from "../../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  getFormattedAmount,
  getFullName,
  isEmpty,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../../../../../helpers/utils";
import { useMediaQueries } from "../../../../../../../../hooks/useMediaQueries";
import { toggleLoader } from "../../../../../../../../redux/slices/appState/appStateSlice";
import {
  ADD_PROGRAM_FEE_AND_EXPENSE_TYPES,
  ProgramFeeTabFormKey,
} from "../../../../../../modelsAndSchemas/Models";
import { addFeesAndExpensesSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { useSiteCore } from "./../../../../../../../../hooks/useSiteCore";
import { ViewFeeExpenseModalContainer } from "./ViewFeeExpenseModal.style";

interface ViewFeeExpenseModalProps {
  open: boolean;
  programExpenseId: string;
  billingCurrencyName: string;
  onClose: () => void;
}

type feeExpenseItem = {
  label: string;
  value?: string | string[] | React.ReactNode;
  showEllipsis?: boolean;
};

type FieldValues = {
  header: string;
  name?: string;
  items: feeExpenseItem[];
  gridSize?: { md: number; sm: number; xs: number; lg?: number };
};

const SectionBox: React.FC<{
  header?: string;
  items: feeExpenseItem[];
  gridSize: { md: number; sm: number; xs: number; lg?: number };
  index?: number;
  className?: string;
}> = ({
  header,
  items,
  gridSize = { lg: 4, md: 3, xs: 12, sm: 3 },
  className = "",
}) => {
  const { isMobile } = useMediaQueries();
  return (
    <MuiBox className={`container-box ${className}`}>
      {header && (
        <MuiTypography variant="h2Bold" className="container-header">
          {header}
        </MuiTypography>
      )}
      <MuiGrid
        container
        spacing={isMobile ? 0 : 2}
        rowSpacing={isMobile ? 2 : 5}
        className="configuration-container"
      >
        {items.map((item) => (
          <MuiGrid key={item.label} container size={gridSize}>
            <MuiDoubleLabel
              label={item.label}
              value={item.value}
              replaceEmptyWithDash={true}
              isValueWithEllipsis={false}
            />
          </MuiGrid>
        ))}
      </MuiGrid>
    </MuiBox>
  );
};

const ViewFeeExpenseModal: React.FC<ViewFeeExpenseModalProps> = ({
  open,
  programExpenseId,
  billingCurrencyName,
  onClose,
}) => {
  const [tabIndex, setTabIndex] = useState(0);
  const gridSize = { lg: 4, md: 6, xs: 12, sm: 6 };
  const [summary, setSummary] = useState<FieldValues[]>([]);
  const [detailsSections, setDetailsSections] = useState<
    FieldValues[] | null
  >();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  const { isMobile } = useMediaQueries();
  const {
    mutate: getProgramExpenseById,
    data: programExpense,
    isPending,
  } = useGetProgramExpenseById();
  const { fetchLabel, commonLabels } = useSiteCore();
  const methods = useForm({
    mode: "all",
  });
  const { setValue } = methods;
  const dispatch = useDispatch();
  const billingCurrencyFormat = CURRENCY_FORMAT_MAP[billingCurrencyName];
  // --- CONSTANTS ---
  const taxDetailsFormKeys: TaxDetailsFormKeyProps = {
    programFeeKey: ProgramFeeTabFormKey.fee,
    taxKey: ProgramFeeTabFormKey.tax,
    taxPercentageKey: ProgramFeeTabFormKey.taxPercentage,
    taxAmountWithFormatKey: ProgramFeeTabFormKey.taxAmountWithFormat,
    taxAmountKey: ProgramFeeTabFormKey.taxAmount,
    taxEntriesKey: ProgramFeeTabFormKey.taxEntries,
    taxSearchKey: ProgramFeeTabFormKey.taxSearch,
  };

  // --- HELPER FUNCTIONS ---
  const getLabel = (key: keyof typeof addFeesAndExpensesSiteCoreKeys) => {
    return fetchLabel(
      addFeesAndExpensesSiteCoreKeys.addFeesAndExpenses,
      addFeesAndExpensesSiteCoreKeys[key],
    );
  };

  const isProgramOrProjectFee = (expense: ViewFeeOrExpenseDetails) => {
    return (
      expense?.type === ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_OR_PROJECT_FEE
    );
  };

  // Reusable billing details section
  const getBillingDetailsSection = (expenseDetails: ViewExpenseDetails) => {
    const isCurrencyMismatch =
      billingCurrencyName !== expenseDetails.feeCurrency?.name;

    const items = [
      {
        label: getLabel("billingDetailsCurrency"),
        value: billingCurrencyName,
      },
      ...(isCurrencyMismatch
        ? [
            {
              label: getLabel("billingDetailsExchangeRate"),
              value: expenseDetails.exchangeRate
                ? `${billingCurrencyName} ${expenseDetails.exchangeRate}`
                : "",
            },
          ]
        : []),
      {
        label: getLabel("billingDetailsAmount"),
        value: getFormattedValue(expenseDetails.billingAmount),
      },
    ];

    return {
      header: getLabel("billingDetailsHeading"),
      items,
    };
  };

  // Reusable comment section
  const getCommentSection = (expenseDetails: ViewExpenseDetails) => ({
    header: "",
    items: [
      {
        label: commonLabels.comment,
        value: (
          <MuiDoubleLabel
            label={""}
            replaceEmptyWithDash={true}
            value={
              isNotEmpty(expenseDetails.comments) ? (
                <MuiViewMorePopOver
                  anchorText={commonLabels.viewMore}
                  message={expenseDetails.comments}
                />
              ) : (
                ""
              )
            }
          />
        ),
      },
    ],
    gridSize: viewMorePopOverGridSize,
  });

  const getFormattedValue = (
    value: number | undefined,
    currencyName?: string,
  ) => {
    if (value && isEmpty(value)) return EMPTY_VALUE_DASH;
    const formattedAmount = `${currencyName ? currencyName : billingCurrencyName} ${getFormattedAmount(value ?? 0, billingCurrencyFormat?.locale)}`;
    return formattedAmount;
  };

  // --- EFFECTS ---
  useEffect(() => {
    getProgramExpenseById(programExpenseId);
  }, []);

  useEffect(() => {
    if (!programExpense) return;
    setValue(
      taxDetailsFormKeys.programFeeKey,
      programExpense?.expenseDetails.feeAmount,
    );
    setValue(taxDetailsFormKeys.taxEntriesKey, programExpense?.taxDetails);
  }, [programExpense]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isPending,
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    };
  }, [isPending]);

  useEffect(() => {
    if (
      programExpense &&
      isNotEmpty(programExpense) &&
      isProgramOrProjectFee(programExpense)
    ) {
      const { expenseDetails } = programExpense;
      setSummary([
        {
          header: getLabel("summaryTab"),
          items: [
            {
              label: getLabel("summaryTabAmount"),
              value: getFormattedValue(expenseDetails.billingAmount),
              showEllipsis: false,
            },
            {
              label: getLabel("summaryTabTotalTaxAmount"),
              value: getFormattedValue(expenseDetails.tax),
              showEllipsis: false,
            },
            {
              label: getLabel("summaryTabTotalBillingAmount"),
              value: getFormattedValue(expenseDetails.totalAmount),
              showEllipsis: false,
            },
          ],
        },
      ]);
      setDetailsSections([
        {
          header: "",
          items: [
            { label: getLabel("feeName"), value: expenseDetails.feeName },
            {
              label: getLabel("transactionDate"),
              value: convertToUserFormat(
                expenseDetails.transactionDate,
                userCountryDateFormat,
              ),
            },
          ],
        },
        {
          header: getLabel("feeDetailsHeading"),
          items: [
            {
              label: getLabel("feeDetailsType"),
              value: expenseDetails.feeType?.name,
            },
            {
              label: getLabel("feeDetailsCurrency"),
              value: expenseDetails.feeCurrency?.name,
            },
            {
              label: getLabel("feeDetailsAmount"),
              value: getFormattedValue(
                expenseDetails.feeAmount,
                expenseDetails.feeCurrency?.name,
              ),
            },
          ],
        },
        getBillingDetailsSection(expenseDetails),
        getCommentSection(expenseDetails),
      ]);
    }
    if (
      programExpense?.type === ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_EXPENSE
    ) {
      const { expenseDetails } = programExpense;
      setSummary([
        {
          header: getLabel("summaryTab"),
          items: [
            {
              label: getLabel("summaryTabAmount"),
              value: getFormattedValue(expenseDetails.feeAmount),
            },
            {
              label: getLabel("summaryTabExchangeRate"),
              value: expenseDetails.exchangeRate
                ? `${billingCurrencyName} ${expenseDetails.exchangeRate}`
                : "",
            },
            {
              label: getLabel("summaryTabBillingAmount"),
              value: getFormattedValue(expenseDetails.billingAmount),
            },
            {
              label: getLabel("summaryTabTotalTaxAmount"),
              value: getFormattedValue(expenseDetails.tax),
            },
            {
              label: getLabel("summaryTabTotalInvoiceAmount"),
              value: getFormattedValue(expenseDetails.totalAmount),
            },
          ],
        },
      ]);
      setDetailsSections([
        {
          header: "",
          items: [
            { label: getLabel("expenseName"), value: expenseDetails.feeName },
            {
              label: getLabel("transactionDate"),
              value: convertToUserFormat(
                expenseDetails.transactionDate,
                userCountryDateFormat,
              ),
            },
            {
              label: getLabel("incurredBy"),
              value:
                isNotEmpty(expenseDetails.incurredBy) &&
                expenseDetails.incurredBy?.firstName &&
                expenseDetails.incurredBy?.lastName
                  ? getFullName(
                      expenseDetails.incurredBy?.firstName,
                      expenseDetails.incurredBy?.lastName,
                      true,
                    )
                  : "",
            },
          ],
        },
        {
          header: getLabel("expenseDetailsHeading"),
          items: [
            {
              label: getLabel("expenseDetailsCategory"),
              value: expenseDetails.expenseCategory?.name,
            },
            {
              label: getLabel("expenseDetailsType"),
              value: expenseDetails.expenseType?.name,
            },
            {
              label: getLabel("expenseDetailsUnitPrice"),
              value: expenseDetails?.unitRate?.toString(),
            },
            {
              label: getLabel("expenseDetailsQuantity"),
              value: expenseDetails?.quantity?.toString(),
            },
            {
              label: getLabel("expenseDetailsCurrency"),
              value: expenseDetails.feeCurrency.name,
            },
            {
              label: getLabel("expenseDetailsAmount"),
              value: getFormattedValue(expenseDetails.feeAmount),
            },
          ],
        },
        getBillingDetailsSection(expenseDetails),
        getCommentSection(expenseDetails),
      ]);
    }
  }, [programExpense]);

  const tabIdGeneral = `generalTab`;
  const tabIdTax = `taxTab`;
  const tabs: ITabs[] = [
    {
      id: tabIdGeneral,
      label: getLabel("generalTab"),
    },
    {
      id: tabIdTax,
      label: getLabel("taxTab"),
    },
  ];

  return (
    <FormProvider {...methods}>
      <MuiModal
        open={open}
        onClose={onClose}
        title={replaceHashWithValue(getLabel("viewFeesAndExpensesHeading"), {
          FEEOREXPENSENAME: programExpense?.expenseDetails.feeName ?? "",
          TYPE: programExpense?.type
            ? fetchLabel(
                addFeesAndExpensesSiteCoreKeys.addFeesAndExpenses,
                programExpense?.type,
              )
            : "",
        })}
        showPrimaryButton={false}
        secondaryButtonTitle={commonLabels.close}
        onSecondaryButtonClick={onClose}
        tertiaryButtonTitle=""
        hasLargeContent
        className="view-fee-expense-modal"
      >
        <ViewFeeExpenseModalContainer className="view-fee-expense-modal-container">
          {summary.map((item) => (
            <SectionBox
              header={item.header}
              items={item.items}
              gridSize={gridSize}
              key={`${item.header}-${item.items[0]?.label ?? "section"}`}
            />
          ))}
          <MuiTabs
            tabs={tabs}
            value={tabIndex}
            onChange={(_: React.ChangeEvent<{}>, newValue: number) =>
              setTabIndex(newValue)
            }
            tabsTestIds={tabs.map((tab) => tab.id)}
            className="tabs-container"
          >
            <MuiTabPanel
              panelId="general-panel"
              value={tabIndex}
              index={0}
              className="tab-panel-details"
              ariaLabelledById={tabIdGeneral}
            >
              <>
                {detailsSections?.map((section) => (
                  <MuiGrid
                    container
                    columnSpacing={isMobile ? 0 : 2}
                    rowSpacing={isMobile ? 0 : 5}
                    key={`${section.header}-${section.items[0]?.label ?? "section"}`}
                  >
                    <SectionBox
                      header={section.header}
                      items={section.items}
                      gridSize={section.gridSize ?? gridSize}
                      className="border-bottom-line"
                    />
                  </MuiGrid>
                ))}
              </>
            </MuiTabPanel>
            <MuiTabPanel
              panelId="fees-tab-history"
              value={tabIndex}
              index={1}
              className="tab-panel-history"
              ariaLabelledById={tabIdTax}
            >
              <TaxDetails
                pageId={"addFeesAndExpenseTaxForm"}
                currencyFormat={billingCurrencyFormat}
                currencyName={billingCurrencyName}
                forceUpdate={() => {}}
                currencyId={programExpense?.expenseDetails.feeCurrency.id ?? ""}
                isInEditMode={false}
                isInViewMode={true}
                taxDetailsFormKeys={taxDetailsFormKeys}
                programOrProjectFee={
                  programExpense?.expenseDetails.billingAmount.toString() || ""
                }
              />
            </MuiTabPanel>
          </MuiTabs>
        </ViewFeeExpenseModalContainer>
      </MuiModal>
    </FormProvider>
  );
};

export default ViewFeeExpenseModal;
